import { skipToken } from "@reduxjs/toolkit/query"
import { Mic } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { StreamingChatInput } from "../../components/streaming/streaming-input.tsx"
import { useStreaming } from "../../hooks/use-streaming.ts"
import NoResultsFound from "@/components/not-found/no-results-found.tsx"
import PageContentWrapperAI from "@/components/page-content-wrappers/page-content-wrapper-ai.tsx"
import ScrollToBottomButton from "@/components/scroll-to-bottom/scroll-to-bottom.tsx"
import Switch from "@/components/shadcn/switch/switch.tsx"
import MobileTopBar from "@/components/top-bars/mobile-top-bar/mobile-top-bar.tsx"
import { useGetAiAssistantThreadQuery } from "@/features/assistant/api/assistant-api.ts"
import { AssistantDrawer } from "@/features/assistant/components/assistant-drawer/assistant-drawer.tsx"
import { LimitWidget } from "@/features/assistant/components/limit-widget/limit-widget.tsx"
import { StreamingChatBubbleWrapper } from "@/features/assistant/components/streaming/streaming-chat-bubble-wrapper.tsx"
import { AI_ASSISTANT_VIEW } from "@/features/assistant/routes/route-list.ts"
import { type AssistantMessage } from "@/features/assistant/types/assistant.ts"
import { DeepgramSpeechRecognition } from "@/utils/speech/deepgram-speech"
import { cn } from "@/utils/styles"

export const StreamingThreadView = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const initialMessage = location.state?.initialMessage
  const suggested_question_id = location.state?.suggested_question_id

  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [limitReached, setLimitReached] = useState(false)

  // Streaming state
  const [isStreaming, setIsStreaming] = useState(false)
  const isStreamingRef = useRef(false)
  isStreamingRef.current = isStreaming

  const [streamingContent, setStreamingContent] = useState("")
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(
    null,
  )
  const [_, setConnectionStatus] = useState<string>("")

  const { threadId } = useParams<{ threadId: string }>()
  const { data, isLoading, isError } = useGetAiAssistantThreadQuery(
    threadId ?? skipToken,
  )
  const containerRef = useRef<HTMLDivElement>(null)

  // Voice mode state (per thread, frontend only)
  const [voiceMode, setVoiceMode] = useState<Record<string, boolean>>({})
  const isVoiceMode = !!(threadId && voiceMode[threadId])
  const handleToggleVoiceMode = () => {
    if (threadId)
      setVoiceMode((prev) => ({ ...prev, [threadId]: !prev[threadId] }))
  }

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      })
    }
  }, [])

  const createTempMessage = useCallback(
    (message: string): AssistantMessage => ({
      id: "temp",
      user_message: message,
      gpt_response: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      thread: threadId!,
    }),
    [threadId],
  )

  const { startStreaming, stopStreaming } = useStreaming({
    onConnected: () => {
      setConnectionStatus("Connected")
    },
    onContent: (content) => {
      setStreamingContent((prev) => prev + content)
      setTimeout(scrollToBottom, 10)
    },
    onComplete: (fullResponse) => {
      setIsStreaming(false)
      setStreamingContent("")
      setStreamingMessageId(null)
      setConnectionStatus("")

      setMessages((prevMessages) => {
        const newMessages = [...prevMessages]
        const index = newMessages.findIndex((msg) => msg.id === "temp")
        if (index !== -1) {
          newMessages[index] = {
            ...newMessages[index],
            id: `msg_${Date.now()}`,
            gpt_response: fullResponse,
          }
        }
        return newMessages
      })
      setTimeout(scrollToBottom, 100)

      // TTS: Only speak the just-finished response if in voice mode
      if (isVoiceMode && fullResponse) {
        interruptTTS()
        const utter = new window.SpeechSynthesisUtterance(fullResponse)
        utter.lang = i18n.language || "en-US"
        ttsRef.current = utter
        synth.speak(utter)
      }
    },
    onError: (error) => {
      setIsStreaming(false)
      setStreamingContent("")
      setStreamingMessageId(null)
      setConnectionStatus("")

      if (error.includes("429")) {
        setLimitReached(true)
        toast.error(t("rateMessageExceeded"))
      } else {
        toast.error(`Streaming error: ${error}`)
      }

      // Remove temp message on error
      setMessages((prev) => prev.filter((msg) => msg.id !== "temp"))
    },
    onStatus: (status) => {
      setConnectionStatus(status)
    },
  })

  const handleSendMessage = useCallback(
    (message: string) => {
      if (!threadId) return

      // Use the ref for an immediate, up-to-date check.
      if (isStreamingRef.current) {
        console.warn(
          "Attempted to send a message while another was already in-flight.",
        )
        return
      }

      const tempMessage = createTempMessage(message)
      setMessages((prev) => [...prev, tempMessage])
      setIsStreaming(true)
      setStreamingContent("")
      setStreamingMessageId("temp")
      setTimeout(scrollToBottom, 50)
      startStreaming(threadId, message, suggested_question_id)
    },
    [
      threadId,
      createTempMessage,
      startStreaming,
      suggested_question_id,
      scrollToBottom,
    ],
  )

  const handleStopStreaming = useCallback(() => {
    stopStreaming()
    setIsStreaming(false)
    setStreamingContent("")
    setStreamingMessageId(null)
    setConnectionStatus("")

    // Remove temp message
    setMessages((prev) => prev.filter((msg) => msg.id !== "temp"))
  }, [stopStreaming])

  // Load existing messages
  useEffect(() => {
    if (!initialMessage && threadId && !isLoading && data) {
      setMessages(data.messages)
      if (data.rate_limit_info.messages_remaining === 0) {
        setLimitReached(true)
      }
    }
  }, [threadId, isLoading, data, initialMessage])

  // Send initial message
  useEffect(() => {
    if (
      initialMessage &&
      threadId &&
      !isLoading &&
      data &&
      data.messages.length === 0 &&
      messages.length === 0
    ) {
      handleSendMessage(initialMessage)
    }
  }, [
    data,
    initialMessage,
    isLoading,
    messages.length,
    threadId,
    handleSendMessage,
  ])

  // Scroll management
  useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = containerRef.current
        setShowScrollButton(scrollTop + clientHeight < scrollHeight - 100)
      }
    }

    const container = containerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
    }

    setTimeout(scrollToBottom, 500)

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll)
      }
    }
  }, [scrollToBottom])

  const handleBackClick = useCallback(() => {
    stopStreaming()
    navigate(AI_ASSISTANT_VIEW)
  }, [navigate, stopStreaming])

  useEffect(() => {
    if (threadId && location.state?.voiceMode) {
      setVoiceMode((prev) => ({ ...prev, [threadId]: true }))
    }
  }, [threadId, location.state])

  const { i18n } = useTranslation()

  // TTS state
  const ttsRef = useRef<SpeechSynthesisUtterance | null>(null)
  const synth = window.speechSynthesis

  // Interrupt TTS if user starts speaking
  const interruptTTS = () => {
    if (synth && synth.speaking) {
      synth.cancel()
      ttsRef.current = null
    }
  }

  // --- Voice Input State and Logic ---

  const [voiceInput, setVoiceInput] = useState("")
  const finalTranscriptRef = useRef("")
  const recognitionRef = useRef<DeepgramSpeechRecognition | null>(null)
  const [listeningState, setListeningState] = useState<
    "idle" | "listening" | "stopping"
  >("idle")
  const isListening = listeningState === "listening"

  const handleSubmitVoiceInput = useCallback(() => {
    const textToSubmit = finalTranscriptRef.current.trim()
    finalTranscriptRef.current = ""
    setVoiceInput("")

    if (textToSubmit.split(/\s+/).length >= 2) {
      handleSendMessage(textToSubmit)
    }
  }, [handleSendMessage])

  const stopListening = useCallback(() => {
    setListeningState((currentState) => {
      if (currentState === "listening" && recognitionRef.current) {
        recognitionRef.current.stop()
        return "stopping"
      }
      return currentState
    })
  }, [])

  const startListening = useCallback(async () => {
    if (listeningState !== "idle") return

    interruptTTS()
    finalTranscriptRef.current = ""
    setVoiceInput("")

    const recognition = new DeepgramSpeechRecognition({
      lang: i18n.language?.split("-")[0] || "ro",
      continuous: true,
      interimResults: true,
      onResult: (transcript: string, isFinal: boolean) => {
        if (isFinal) {
          // A final transcript segment has arrived. Append it to our permanent ref.
          finalTranscriptRef.current = (
            finalTranscriptRef.current +
            " " +
            transcript
          ).trim()
          setVoiceInput(finalTranscriptRef.current)
        } else {
          // This is an interim result. Display the final parts + this interim part.
          setVoiceInput((finalTranscriptRef.current + " " + transcript).trim())
        }
      },
      onSpeechEnd: stopListening,
      onEnd: () => {
        setListeningState("idle")
        recognitionRef.current = null
        handleSubmitVoiceInput()
      },
      onError: (error: string) => {
        console.error("Deepgram error:", error)
        setListeningState("idle")
        recognitionRef.current = null
      },
    })

    recognitionRef.current = recognition

    try {
      setListeningState("listening")
      await recognition.start()
    } catch (error) {
      console.error("Error starting Deepgram recognition:", error)
      setListeningState("idle")
      recognitionRef.current = null
    }
  }, [i18n.language, interruptTTS, stopListening, handleSubmitVoiceInput])

  const handleMicClick = useCallback(() => {
    if (listeningState === "listening") {
      stopListening()
    } else if (listeningState === "idle") {
      startListening()
    }
  }, [listeningState, startListening, stopListening])
  // --- End Voice Input Logic ---

  if (isError) {
    return (
      <>
        <MobileTopBar
          title={t("intelligentAssistant")}
          icon={<AssistantDrawer threadId={threadId} />}
          onIconClick={() => {}}
          onBackClick={handleBackClick}
        />
        <PageContentWrapperAI
          ref={containerRef}
          isLoading={isLoading}
          className={"grid items-center"}
        >
          <NoResultsFound message={t("anErrorOccurred")} />
        </PageContentWrapperAI>
      </>
    )
  }

  return (
    <>
      <MobileTopBar
        title={t("intelligentAssistant")}
        icon={<AssistantDrawer threadId={threadId} />}
        onIconClick={() => {}}
        onBackClick={handleBackClick}
        rightContent={
          threadId && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">{t("vocalAi")}</span>
              <Switch
                checked={isVoiceMode}
                onCheckedChange={handleToggleVoiceMode}
              />
            </div>
          )
        }
      />

      <PageContentWrapperAI ref={containerRef} isLoading={isLoading}>
        {!isLoading && threadId && messages && messages.length > 0 && (
          <div className="space-y-1">
            {messages.map((message, index) => (
              <StreamingChatBubbleWrapper
                message={message}
                key={`${message.id}-${index}`}
                isStreaming={isStreaming && message.id === streamingMessageId}
                streamingContent={streamingContent}
                showTypingIndicator={
                  isStreaming &&
                  message.id === streamingMessageId &&
                  !streamingContent
                }
              />
            ))}
          </div>
        )}
      </PageContentWrapperAI>

      {showScrollButton && (
        <ScrollToBottomButton
          onClick={scrollToBottom}
          bottom={"calc(var(--ai-chat-input-height-mobile))"}
        />
      )}

      {threadId &&
        !limitReached &&
        (isVoiceMode ? (
          <div className="flex flex-col items-center justify-center gap-1 py-2">
            <div className="relative flex h-16 w-16 items-center justify-center">
              {isListening && (
                <span
                  className="absolute h-full w-full rounded-full bg-brown-200"
                  style={{
                    animation: "pulseGlow 1.5s ease-in-out infinite",
                    opacity: 0.6,
                  }}
                />
              )}
              <button
                type="button"
                onClick={handleMicClick}
                className={cn(
                  "relative flex h-full w-full items-center justify-center rounded-full border shadow-sm transition-colors duration-200 focus:outline-none",
                  isListening
                    ? "border-brown-400 bg-brown-50 text-brown-700"
                    : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50 focus:ring-2 focus:ring-brown-300",
                )}
                aria-label={
                  isListening ? t("stopListening") : t("startListening")
                }
              >
                <Mic size={32} />
              </button>
            </div>
            {(isListening || voiceInput) && (
              <div
                className="mt-2 flex min-h-[24px] max-w-xs items-center justify-center overflow-hidden rounded-full bg-zinc-100 px-3 py-1 text-center text-xs text-zinc-500"
                style={{ width: 220 }}
              >
                <span
                  className="animate-slide-left inline-block whitespace-nowrap"
                  style={{ transition: "transform 0.3s" }}
                >
                  {voiceInput
                    ? voiceInput.trim().split(/\s+/).slice(-6).join(" ")
                    : isListening
                      ? t("listening")
                      : ""}
                </span>
              </div>
            )}
          </div>
        ) : (
          <StreamingChatInput
            onSendMessage={handleSendMessage}
            disabled={false}
            isStreaming={isStreaming}
            onStopStreaming={handleStopStreaming}
            placeholder={t("yourMessage")}
          />
        ))}

      {limitReached && data && (
        <div className="px-2">
          <LimitWidget reset_date={data?.rate_limit_info.reset_date} />
        </div>
      )}

      <style
        dangerouslySetInnerHTML={{
          __html: `
          @keyframes pulseGlow {
            0%, 100% {
              transform: scale(1);
              opacity: 0.6;
            }
            50% {
              transform: scale(1.2);
              opacity: 0.3;
            }
          }
        `,
        }}
      />
    </>
  )
}
